"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.calculateStatus = exports.computeNextStatus = exports.isCartExchangeable = exports.getCartEntryPaidAmount = exports.getCartEntryAddonsString = exports.cartEntryHasAddons = exports.cartEntryHasDiscountNote = exports.cartEntryHasDiscount = exports.cartEntryHasNote = exports.getCartEntryProductName = exports.getCurrency = exports.changeCartEntryAmount = exports.editItemFromCart = exports.removeItemFromCart = exports.addItemToCart = exports.updateCartTotal = exports.calculateCartChange = exports.calculateCartEntryAddons = exports.calculateCartEntryDiscount = exports.calculateCartEntryPrice = exports.getDiscountPercentFromItem = exports.createCartEntryStatus = exports.createCartEntry = exports.getCartEntryId = exports.getCartEntryIdSimple = exports.emptyCart = exports.createCart = void 0;
const utils_1 = require("./utils");
const products_1 = require("./products");
/**
 * Create cart obj to be use for POS/KDS/WAITER
 * @param  {string} merchantId - ObjectId of the merchant
 * @param  {any} detailDevice - device obj
 * @param  {number} index - cart index in the cart list array
 * @param  {string} orderSource - [webpos, pos, kds, waiter, external]
 * @param  {number} lastTransaction - last transaction number
 * @param  {boolean} cartAsTable - cart is using as table
 * @returns {Cart} New Cart obj
 */
const createCart = (merchantId, detailDevice, index, orderSource, lastTransaction = 1, cartAsTable = false) => {
    const { _id, device_id, store } = detailDevice;
    const now = new Date();
    const cart = {
        merchant: merchantId,
        store: store._id,
        device: _id,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        date: now,
        currency: store.currency,
        order_source: orderSource,
        receipt_id: store.store_id + '-' + device_id + '-' + now.getTime(),
        receipt_number: lastTransaction,
        scanned_codes: [],
        customer: null,
        employee: { _id: null, name: 'None' },
        items: [],
        total: 0,
        sub_total: 0,
        discount: 0,
        change: 0,
        reduction: {
            reduction: 0,
            percentage: 0,
            numeric: 0
        },
        payments: [],
        paid: 0,
        extras: {
            deviceCartNumber: index,
            deviceCartName: `${cartAsTable ? 'TABLE ' : 'CART '} ${index}`,
            appVersion: process.env.REACT_APP_VERSION,
            cartStartedAt: now,
            drop_ship: false,
            takeaway: false,
            covers: 0,
            orderSource,
            age_verification: { minAge: 0 }
        }
    };
    return cart;
};
exports.createCart = createCart;
/**
 * Remove all items, and cleanup all fields of the cart (returns to initial state)
 * @param  {Cart} cart - Cart that want to be updated
 * @returns {Cart} - Cart passed as parameter updated to initial state
 */
const emptyCart = (cart) => {
    const now = new Date();
    const cleannedCart = Object.assign(Object.assign({}, cart), { date: now, receipt_id: cart.receipt_id
            .split('-')
            .slice(0, -1)
            .concat(`${+now}`)
            .join('-'), receipt_number: cart.receipt_number + 1, customer: null, employee: { _id: null, name: 'None' }, items: [], total: 0, sub_total: 0, discount: 0, change: 0, reduction: {
            reduction: 0,
            percentage: 0,
            numeric: 0
        }, payments: [], paid: 0, extras: Object.assign(Object.assign({}, cart.extras), { takeaway: false }) });
    return cleannedCart;
};
exports.emptyCart = emptyCart;
const getCartEntryIdSimple = (item) => {
    return (0, exports.getCartEntryId)(item).split(':')[0];
};
exports.getCartEntryIdSimple = getCartEntryIdSimple;
/**
 * Generate an unique id (mongo like) to given cartEntry object
 * @param  {CartEntry} item - item inside a cart
 * @returns {string} unique id for cartEntry
 */
const getCartEntryId = (item) => {
    if (!item) {
        return '';
    }
    // calculate cart entry id combining product id,
    // variant id, sorted addons id and addons amount
    // example: 213127632718638216-v12-a12-a23-a43
    // product id
    const pId = item.product._id;
    // variant id
    const vId = item.product.current_variant._id;
    // addons
    // let aIds = ''
    let aId = '';
    if (item.product.current_addons !== undefined && item.product.current_addons.length > 0) {
        // aIds = item.product.current_addons.sort(obj => obj._id)
        aId = item.product.current_addons.reduce((acc, current) => {
            return acc + ':a' + current._id + 'x' + current.amount;
        }, '');
    }
    // discount key
    const dAmount = item.product.discount && !item.discount_note ? ':d' + item.product.discount : null;
    // var pPrice = this.getPrice();
    let pPrice = item.product.current_variant.price || item.product.price;
    if (item.discount_note) {
        pPrice = pPrice / (1 - item.product.discount);
    }
    // note key
    let nHash = '';
    if (item.note) {
        nHash = item.note ? ':n' + (0, utils_1.hashCode)(item.note) : null;
    }
    // non_revenue_key
    let nRHash = '';
    if (item.product.extras && item.product.extras.non_revenue_info) {
        nRHash = item.product.extras.non_revenue_info
            ? ':nr' + (0, utils_1.hashCode)(item.product.extras.non_revenue_info)
            : null;
    }
    // generate id (ex: 213127632718638216:v12:a12:a23:a43)
    // add price to the ID for dynamic pricing
    const id = pId +
        (vId ? ':v' + vId : '') +
        (aId || '') +
        (dAmount || '') +
        (pPrice ? ':p' + pPrice : '') +
        (nHash || '') +
        (nRHash || '');
    // return id
    return id;
};
exports.getCartEntryId = getCartEntryId;
/**
 * Create new cart entry based on a passed product/variant to be add to the cart's items list
 * @param  {any} product - product obj
 * @param  {any} variant - variant obj
 * @param  {string} storeId - ObjectId of the store
 * @param  {number} amount - amount of the products to be add
 * @param  {any} addons - Array with selected addons
 * @returns {any} cart entry obj
 */
const createCartEntry = (product, variant, storeId, amount = 1, addons = [], status) => {
    const cartEntry = {
        product: Object.assign(Object.assign({}, product), { current_addons: addons, current_variant: Object.assign(Object.assign({}, variant), { price: (0, products_1.getProductPrice)(product, variant._id, storeId), cost_price: (0, products_1.getProductPrice)(product, variant._id, storeId, 'cost_price') }) }),
        amount,
        note: '',
        discount_note: '',
        status: (0, exports.createCartEntryStatus)(amount, status)
    };
    return cartEntry;
};
exports.createCartEntry = createCartEntry;
/**
 * Create status array with new item for cart's items that don't have it yet
 * @param  {number} amount
 * @returns {any} array of item status obj
 */
const createCartEntryStatus = (amount, status) => {
    return [{ last_update: new Date().toISOString(), state: 'new', amount }];
};
exports.createCartEntryStatus = createCartEntryStatus;
/**
 * Get the percentage of discount applied in an item
 * @param  {CartEntryProduct} product - obj inside cart items
 * @returns {number} discount percentage of item (e.g: 0.2)
 */
const getDiscountPercentFromItem = (product) => {
    const { price } = product.current_variant;
    if (!!product.discount) {
        return product.discount;
    }
    else if (!!product.reduction) {
        const { numeric, percentage } = product.reduction;
        if (!!numeric || !!percentage) {
            return !!percentage ? percentage : numeric / price;
        }
    }
    return 0;
};
exports.getDiscountPercentFromItem = getDiscountPercentFromItem;
/**
 * Return cart entry total price (amount * product price (with/without discount))
 * @param  {CartEntry} item - Cart entry obj
 * @param  {boolean} noDiscount - If should considerer discount on this calculation
 * @returns {number} cart entry total price
 */
const calculateCartEntryPrice = (item, noDiscount = false) => {
    const { product } = item;
    const { price } = product.current_variant;
    const addonsPrice = (0, exports.calculateCartEntryAddons)(item);
    const discount = 1 - (0, exports.getDiscountPercentFromItem)(product);
    const productDeposit = (0, products_1.getDepositPrice)(product);
    const currentPrice = (price + addonsPrice - productDeposit) * (noDiscount ? 1 : discount) * item.amount;
    return currentPrice + productDeposit * item.amount;
};
exports.calculateCartEntryPrice = calculateCartEntryPrice;
/**
 * Return cart entry discount price
 * @param  {CartEntry} item - Cart entry obj
 * @returns {number} cart entry discount
 */
const calculateCartEntryDiscount = (item) => {
    return (0, exports.calculateCartEntryPrice)(item, true) - (0, exports.calculateCartEntryPrice)(item);
};
exports.calculateCartEntryDiscount = calculateCartEntryDiscount;
/**
 * Return cart entry's addons total price of all addons on it
 * @param  {CartEntry} item - Cart entry obj
 * @returns {number} sum of all addons prices of this cart entry
 */
const calculateCartEntryAddons = (item) => {
    const { current_addons } = item.product;
    if (!!current_addons && current_addons.length) {
        return current_addons.reduce((acc, addon) => acc + addon.price * addon.amount, 0);
    }
    return 0;
};
exports.calculateCartEntryAddons = calculateCartEntryAddons;
/**
 * Return the diff between value to be paid and cart's total
 * @param  {Cart} cart - Cart obj
 * @param  {number} toPay - Amount that client will paid
 * @returns {number} Calculate change to be placed on the cart
 */
const calculateCartChange = (cart, toPay) => {
    const { total } = cart;
    if (toPay > total) {
        return toPay - total;
    }
    return 0;
};
exports.calculateCartChange = calculateCartChange;
// export const getCartEntryTaxRate = (item: CartEntry): number => {
//   const { current_variant } = item.product
//   const { rules } = current_variant.tax!
//   return !!rules && rules.length ? rules[0].rate : 0
// }
// export const calculateCartEntryTax = (item: CartEntry): number => {
//   const price =
// }
/**
 * Update total/sub_total/date from given cart
 * @param  {CountrSdk} sdk - Countr sdk obj
 * @param  {Cart} cart - Cart that want to be updated
 * @returns {Cart} - Cart passed as parameter updated with new total/sub_total/date
 */
const updateCartTotal = (sdk, cart) => {
    const cartCopy = Object.assign({}, cart);
    // sdk is used to calculate total, sub_total and taxes
    const { totalAmount, totalAmountPreTax } = sdk.calculateTotal(cartCopy);
    cartCopy.total = totalAmount;
    cartCopy.sub_total = totalAmountPreTax;
    cartCopy.date = new Date();
    return cartCopy;
};
exports.updateCartTotal = updateCartTotal;
/**
 * Adding new cart entry to cart.items list or update existed one
 * @param  {CountrSdk} sdk - Countr sdk obj
 * @param  {Cart} cart - Cart that want to be updated
 * @param  {CartEntry} cartEntry - Item to be add to the cart
 * @returns {Cart} - Cart passed as parameter updated with new item
 */
const addItemToCart = (sdk, cart, cartEntry) => {
    const cartCopy = Object.assign({}, cart);
    const cartEntryId = (0, exports.getCartEntryId)(cartEntry);
    const itemIndex = cartCopy.items.findIndex((item) => (0, exports.getCartEntryId)(item) === cartEntryId);
    if (!cartCopy.items.length) {
        cartCopy.extras = Object.assign(Object.assign({}, cartCopy.extras), { cartStartedAt: new Date() });
    }
    if (itemIndex < 0) {
        cartCopy.items.unshift(cartEntry);
    }
    else {
        cartCopy.items[itemIndex].amount += cartEntry.amount;
        const newStatusIndex = cartCopy.items[itemIndex].status.findIndex((st) => st.state === 'new');
        if (newStatusIndex >= 0) {
            cartCopy.items[itemIndex].status[newStatusIndex].amount += cartEntry.amount;
        }
        else {
            cartCopy.items[itemIndex].status.concat((0, exports.createCartEntryStatus)(cartEntry.amount, (status = 'new')));
        }
        // Removing cartEntry from array and add it again to the beginning of cart items
        const entryCopy = Object.assign({}, cartCopy.items[itemIndex]);
        cartCopy.items.splice(itemIndex, 1);
        cartCopy.items.unshift(entryCopy);
    }
    return (0, exports.updateCartTotal)(sdk, cartCopy);
};
exports.addItemToCart = addItemToCart;
/**
 * Remove item from cart.items list
 * @param  {CountrSdk} sdk - Countr sdk obj
 * @param  {Cart} cart - Cart that want to be updated
 * @param  {number} itemIndex - Index of the item that will be removed
 * @returns {Cart} - Cart passed as parameter updated without item
 */
const removeItemFromCart = (sdk, cart, itemIndex) => {
    const cartCopy = Object.assign({}, cart);
    cartCopy.items.splice(itemIndex, 1);
    return (0, exports.updateCartTotal)(sdk, cartCopy);
};
exports.removeItemFromCart = removeItemFromCart;
/**
 * Edit cart entry from cart.items with a new version of it and push it to the begin of the list
 * @param  {CountrSdk} sdk - Countr sdk obj
 * @param  {Cart} cart - Cart that want to be updated
 * @param  {CartEntry} updatedCartEntry - Item to be updated
 * @param  {number} itemIndex - Index of the item that will be updated
 * @returns {Cart} - Cart passed as parameter updated with update cartEntry
 */
const editItemFromCart = (sdk, cart, updatedCartEntry, itemIndex) => {
    const cartCopy = Object.assign({}, cart);
    cartCopy.items.splice(itemIndex, 1);
    cartCopy.items.unshift(updatedCartEntry);
    return (0, exports.updateCartTotal)(sdk, cartCopy);
};
exports.editItemFromCart = editItemFromCart;
/**
 * Edit cart entry from cart.items increasing or decreasing amount by one
 * @param  {CountrSdk} sdk - Countr sdk obj
 * @param  {Cart} cart - Cart that want to be updated
 * @param  {CartEntry} cartEntry - Item to be updated
 * @param  {string} action - add | remove
 * @returns {Cart} - Cart passed as parameter updated with update cartEntry
 */
const changeCartEntryAmount = (sdk, cart, cartEntry, action) => {
    const cartCopy = Object.assign({}, cart);
    const index = cartCopy.items.findIndex((item) => (0, exports.getCartEntryId)(item) === (0, exports.getCartEntryId)(cartEntry));
    if (action === 'add') {
        cartCopy.items[index].amount++;
    }
    else if (action === 'remove') {
        cartCopy.items[index].amount--;
        if (cartCopy.items[index].amount <= 0) {
            cartCopy.items.splice(index, 1);
        }
    }
    else {
        throw new Error(`Action ${action} has no effect. Please use add or remove.`);
    }
    return (0, exports.updateCartTotal)(sdk, cartCopy);
};
exports.changeCartEntryAmount = changeCartEntryAmount;
/**
 * Get cart currency property
 * @param  {Cart} cart - Cart obj
 * @param  {string} type - symbol | code
 * @returns {string} - returns carts currency symbol or currency code
 */
const getCurrency = (cart, type = 'symbol') => {
    const prop = type === 'symbol' ? 'symbol' : 'code';
    return cart.currency[prop];
};
exports.getCurrency = getCurrency;
/**
 * Gets cart entry product name coposed with variant name (if it isn't default)
 * @param  {CartEntry} item - Cart entry obj
 * @param  {string} language - Language to product name to be translated
 * @returns {string} - returns item coposed name
 */
const getCartEntryProductName = (item, language = 'en') => {
    var _a, _b;
    const productName = (_a = (0, products_1.getProductName)(item.product, language)) !== null && _a !== void 0 ? _a : item.product.name;
    const variant = Object.assign({}, item.product.current_variant);
    const variantName = !item.product.current_variant.default
        ? ` (${(_b = (0, products_1.getVariantName)(variant, language)) !== null && _b !== void 0 ? _b : item.product.current_variant.name})`
        : '';
    return `${productName}${variantName}`;
};
exports.getCartEntryProductName = getCartEntryProductName;
/**
 * Check if cart entry has notes
 * @param  {CartEntry} item - Cart entry obj
 * @returns {boolean} - returns true if cart entry has note
 */
const cartEntryHasNote = (item) => {
    return !!item.note;
};
exports.cartEntryHasNote = cartEntryHasNote;
/**
 * Check if cart entry has discount
 * @param  {CartEntry} item - Cart entry obj
 * @returns {boolean} - returns true if cart entry has discount
 */
const cartEntryHasDiscount = (item) => {
    const { discount, reduction } = item.product;
    return !!discount || !!(reduction === null || reduction === void 0 ? void 0 : reduction.numeric) || !!(reduction === null || reduction === void 0 ? void 0 : reduction.percentage);
};
exports.cartEntryHasDiscount = cartEntryHasDiscount;
/**
 * Check if cart entry has discount reason (business rules)
 * @param  {CartEntry} item - Cart entry obj
 * @returns {boolean} - returns true if cart entry has discount reason (business rules)
 */
const cartEntryHasDiscountNote = (item) => {
    const { reduction } = item.product;
    return !!(reduction === null || reduction === void 0 ? void 0 : reduction.reason);
};
exports.cartEntryHasDiscountNote = cartEntryHasDiscountNote;
/**
 * Check if cart entry has addons
 * @param  {CartEntry} item - Cart entry obj
 * @returns {boolean} - returns true if cart entry has addons
 */
const cartEntryHasAddons = (item) => {
    return !!item.product.current_addons && !!item.product.current_addons.length;
};
exports.cartEntryHasAddons = cartEntryHasAddons;
/**
 * Get cart entry addons list as a string with amount x name and price concated with comma
 * @param  {CartEntry} item - Cart entry obj
 * @param  {string} currencySymbol - Currency symbol to be add to the string
 * @returns {string} - Addons list formatted with amount, name and price
 */
const getCartEntryAddonsString = (item, currencySymbol) => {
    if (!item.product.current_addons.length) {
        return '';
    }
    return item.product.current_addons
        .map((addon) => {
        return `${addon.amount} x ${addon.name} (${currencySymbol} ${addon.price.toFixed(2)})`;
    })
        .join(', ');
};
exports.getCartEntryAddonsString = getCartEntryAddonsString;
/**
 * Get cart entry amount that was already paid (partial payment per item)
 * @param  {Cart} cart - Cart obj
 * @param  {CartEntry} item - Cart entry obj (item)
 * @returns {number} - Amount (quantity) of given item that was already paid
 */
const getCartEntryPaidAmount = (cart, item) => {
    if (!cart.payments.length) {
        return 0;
    }
    return cart.payments.reduce((acc, payment) => {
        const { info } = payment;
        if (!!info && !!info.products) {
            const product = info.products.find((p) => p.id === (0, exports.getCartEntryId)(item));
            return acc + (!!product ? product.amount : 0);
        }
        return acc;
    }, 0);
};
exports.getCartEntryPaidAmount = getCartEntryPaidAmount;
/**
 * Checks if cart is an exchangeable cart (with products to be exchange)
 * @param  {Cart} cart - Cart obj
 * @returns {boolean} - If is an exachangeable cart
 */
const isCartExchangeable = (cart) => {
    const { exchange_source, total_exchanged } = cart.extras;
    return !!exchange_source && !!total_exchanged && total_exchanged < 0;
};
exports.isCartExchangeable = isCartExchangeable;
const computeNextStatus = (item, employee, state, hideCompleted = false, kitchen_categories = [], considerNewStatus = false) => {
    if (!!kitchen_categories && !!kitchen_categories.length) {
        const itemCategories = item.product.categories.map((c) => c._id);
        let shouldUpdate = false;
        itemCategories.forEach((c) => {
            if (kitchen_categories.indexOf(c) >= 0) {
                shouldUpdate = true;
            }
        });
        if (!shouldUpdate) {
            return null;
        }
    }
    const statusExistIndex = item.status.findIndex((currentState) => currentState.state === state);
    const lastStatusIndex = item.status.findIndex((lastState) => lastState.amount > 0);
    if (lastStatusIndex === -1) {
        throw Error(`Not possible to find last status of item ${item.product.name}`);
    }
    const lastStatus = Object.assign({}, item.status[lastStatusIndex]);
    if (hideCompleted && lastStatus.state === 'completed') {
        return null;
    }
    // Return if state is new or pass if considerNewStatus true
    if (considerNewStatus && lastStatus && lastStatus.state === 'new') {
        return null;
    }
    if (!~statusExistIndex) {
        item.status.push({
            state,
            last_update: new Date().toISOString(),
            amount: lastStatus.amount,
            employees: [employee]
        });
        lastStatus.amount = 0;
        item.status[lastStatusIndex] = lastStatus;
    }
    else {
        const newStatusObj = Object.assign({}, item.status[statusExistIndex]);
        newStatusObj.employees = [employee];
        if (lastStatus.state !== state) {
            newStatusObj.amount += lastStatus.amount;
            lastStatus.amount = 0;
        }
        else {
            if (newStatusObj.amount !== lastStatus.amount) {
                lastStatus.amount = 0;
            }
            newStatusObj.amount = lastStatus.amount;
            lastStatus.employees = [employee];
        }
        if (!newStatusObj.amount) {
            // throw Error('We cannot have the new Status Object with Amount equal ZERO!')
        }
        item.status[statusExistIndex] = newStatusObj;
        item.status[lastStatusIndex] = lastStatus;
    }
    lastStatus.last_update = new Date().toISOString();
    return item;
};
exports.computeNextStatus = computeNextStatus;
const calculateStatus = (sdk, items) => {
    return sdk.calculateStatus(items);
};
exports.calculateStatus = calculateStatus;
